/**
 * Mobile Submission Response Handler
 * 
 * Centralized handler for mobile-submission API responses.
 * Invalidates relevant queries and triggers signature modal if needed.
 */

import { QueryClient } from '@tanstack/react-query';

export interface MobileSubmissionResponse {
  success: boolean;
  postingEventId?: number;
  taskId: number;
  evidenceCount: number;
  storeCompleted?: boolean;
  assignmentCompleted?: boolean;
  packCompleted?: boolean;
  assignmentId?: number;
  storeId?: number;
  counts?: {
    storeTasksTotal: number;
    storeTasksCompleted: number;
    assignmentStoresTotal: number;
    assignmentStoresCompleted: number;
  };
}

/**
 * Handle mobile submission response
 * Invalidates queries and optionally triggers signature modal
 */
export function handleMobileSubmissionResponse(
  queryClient: QueryClient,
  response: MobileSubmissionResponse,
  operatorId?: number | string,
  onAssignmentComplete?: () => void
) {
  // Optimistic update: mark store as completed if backend confirms
  if (response.storeCompleted && response.storeId) {
    queryClient.setQueryData(['storeVisits', operatorId], (oldData: any) => {
      if (!oldData || !Array.isArray(oldData)) return oldData;
      return oldData.map((store: any) =>
        store.storeId === response.storeId
          ? { ...store, visitStatus: 'completed' }
          : store
      );
    });
  }

  // Invalidate evidence query for this assignment so View Evidence shows fresh data
  if (response.assignmentId || response.taskId) {
    queryClient.invalidateQueries({ 
      queryKey: ['assignment-evidence', response.assignmentId || response.taskId] 
    });
  }

  // Invalidate all relevant queries
  queryClient.invalidateQueries({ queryKey: ['route-overview'] });
  queryClient.invalidateQueries({ queryKey: ['storeVisits'] });
  queryClient.invalidateQueries({ queryKey: ['store-visits'] });
  queryClient.invalidateQueries({ queryKey: ['operator-stores'] });
  queryClient.invalidateQueries({ queryKey: ['operator-completed-stores'] });
  
  // Invalidate cycle summary to update completion status
  // Use partial matching to invalidate all cycleSummary queries (regardless of operatorId/cycleId)
  queryClient.invalidateQueries({ 
    queryKey: ['cycleSummary'],
    exact: false  // Matches all queries starting with ['cycleSummary']
  });
  
  // If operatorId is available, also try to refetch cycleSummary queries
  if (operatorId) {
    // Force refetch of any cycleSummary queries (will refetch if they exist)
    queryClient.refetchQueries({ 
      queryKey: ['cycleSummary'],
      exact: false
    });
  }
  
  if (operatorId) {
    // Invalidate completion query (useCompletion hook)
    queryClient.invalidateQueries({ queryKey: ['completion', operatorId] });
    queryClient.invalidateQueries({ queryKey: ['route-overview', 'completion', operatorId] });
    queryClient.invalidateQueries({ queryKey: ['operatorTasks', operatorId] });
    
    // Force immediate refetch of completion data
    queryClient.refetchQueries({ queryKey: ['route-overview', 'completion', operatorId] });
  }

  // Trigger signature modal if all stores in assignment are complete
  if (response.assignmentCompleted || response.packCompleted) {
    console.log('[MOBILE_SUBMISSION] Assignment completed - triggering signature modal', {
      assignmentId: response.assignmentId,
      completedStores: response.counts?.assignmentStoresCompleted,
      totalStores: response.counts?.assignmentStoresTotal
    });
    
    if (onAssignmentComplete) {
      onAssignmentComplete();
    }
  }

  // Log completion status
  if (response.storeCompleted) {
    console.log('[MOBILE_SUBMISSION] Store completed', {
      storeId: response.storeId || response.taskId,
      completedTasks: response.counts?.storeTasksCompleted,
      totalTasks: response.counts?.storeTasksTotal
    });
  }
}

